import React from 'react';

import dayjs from '~/timeInit';

import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { TourOfferFormOptions, TourOfferScheduleInput } from '~/types/tours/tourOffer';

import NativeTourRemoveButton from './NativeTourRemoveButton';
import NativeTourScheduleDateField from './NativeTourScheduleDateField';
import { scheduleProblems } from './nativeTourForm';

interface Props {
  value: Array<TourOfferScheduleInput>;
  onChange: (value: Array<TourOfferScheduleInput>) => void;
  options: TourOfferFormOptions | undefined;
}

const DEFAULT_LENGTH_WEEKS = 4;

function newSchedule(): TourOfferScheduleInput {
  const start = dayjs().startOf('hour').toISOString();
  const end = dayjs().startOf('hour').add(DEFAULT_LENGTH_WEEKS, 'week').toISOString();
  return {
    earlyAccessStartDate: null,
    offlineStartDate: start,
    startDate: start,
    endDate: end,
    offlineEndDate: end,
    regions: [],
    whiteLabelBrands: [],
  };
}

/** When and where the tour is on sale: one card per schedule, its dates in the order they happen. */
export default function NativeTourSchedulesEditor({ value, onChange, options }: Props) {
  const regions = options?.scheduleRegions ?? [];
  const brands = options?.scheduleBrands ?? [];
  const earlyAccessBrands = options?.earlyAccessBrands ?? [];
  const regionName = (code: string) => regions.find((region) => region.code === code)?.name ?? code.toUpperCase();
  const update = (index: number, change: Partial<TourOfferScheduleInput>) =>
    onChange(value.map((schedule, i) => (i === index ? { ...schedule, ...change } : schedule)));

  return (
    <Stack spacing={2}>
      {value.length === 0 && (
        <Typography color="text.secondary">No schedules, so the tour isn't on sale anywhere.</Typography>
      )}
      {value.map((schedule, index) => {
        const problems = scheduleProblems(schedule, earlyAccessBrands);
        return (
          <Paper key={schedule.id ?? `new-${index}`} variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2">
                  Schedule {index + 1}
                  {!schedule.id && <Chip size="small" color="primary" variant="outlined" label="New" sx={{ ml: 1 }} />}
                </Typography>
                <Stack direction="row">
                  <Tooltip title="Copy as a new schedule">
                    <IconButton
                      aria-label="Copy schedule"
                      onClick={() => onChange([...value, { ...schedule, id: undefined }])}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <NativeTourRemoveButton
                    label="Remove schedule"
                    onClick={() => onChange(value.filter((_, i) => i !== index))}
                  />
                </Stack>
              </Stack>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <NativeTourScheduleDateField
                    label="Offline start"
                    value={schedule.offlineStartDate}
                    onChange={(offlineStartDate) => update(index, { offlineStartDate: offlineStartDate ?? '' })}
                    error={problems.offlineStartDate}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <NativeTourScheduleDateField
                    label="Early access start"
                    helperText={`Optional, for LUX+ members on ${earlyAccessBrands.join(', ')}`}
                    value={schedule.earlyAccessStartDate}
                    onChange={(earlyAccessStartDate) => update(index, { earlyAccessStartDate })}
                    error={problems.earlyAccessStartDate}
                    required={false}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <NativeTourScheduleDateField
                    label="Start"
                    value={schedule.startDate}
                    onChange={(startDate) => update(index, { startDate: startDate ?? '' })}
                    error={problems.startDate}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <NativeTourScheduleDateField
                    label="End"
                    value={schedule.endDate}
                    onChange={(endDate) => update(index, { endDate: endDate ?? '' })}
                    error={problems.endDate}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <NativeTourScheduleDateField
                    label="Offline end"
                    value={schedule.offlineEndDate}
                    onChange={(offlineEndDate) => update(index, { offlineEndDate: offlineEndDate ?? '' })}
                    error={problems.offlineEndDate}
                  />
                </Grid>
              </Grid>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={regions.map((region) => region.code)}
                value={schedule.regions}
                onChange={(_event, next) => update(index, { regions: next })}
                getOptionLabel={regionName}
                renderTags={(codes, getTagProps) =>
                  codes.map((code, i) => (
                    <Chip {...getTagProps({ index: i })} key={code} size="small" label={regionName(code)} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Regions"
                    required
                    error={!!problems.regions}
                    helperText={problems.regions}
                  />
                )}
              />
              <Box>
                <Button size="small" onClick={() => update(index, { regions: regions.map((region) => region.code) })}>
                  All regions
                </Button>
              </Box>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={brands}
                value={schedule.whiteLabelBrands}
                onChange={(_event, next) => update(index, { whiteLabelBrands: next })}
                renderTags={(codes, getTagProps) =>
                  codes.map((code, i) => <Chip {...getTagProps({ index: i })} key={code} size="small" label={code} />)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Brands"
                    required
                    error={!!problems.whiteLabelBrands}
                    helperText={problems.whiteLabelBrands ?? 'The sites the tour is sold on'}
                  />
                )}
              />
              <Box>
                <Button size="small" onClick={() => update(index, { whiteLabelBrands: brands })}>
                  All brands
                </Button>
              </Box>
            </Stack>
          </Paper>
        );
      })}
      <Box>
        <Button startIcon={<AddIcon />} onClick={() => onChange([...value, newSchedule()])}>
          Add schedule
        </Button>
      </Box>
    </Stack>
  );
}
